import { useCallback, useEffect, useState } from 'react';

import {
  useGetAppSettingsQuery,
  useSaveAppSettingsMutation,
} from '../../../../../gql/types';
import { GET_APP_SETTINGS_QUERY } from '../../../../settings/graphql/queries';
import { DateRangeType } from '../../../types/phlebotomist-dashboard';
import {
  DEFAULT_RECRUITER_VIEW_OPTIONS,
  IRecruiterViewOptions,
  RECRUITER_SETTINGS_TAB,
} from '../config/viewOptions';
import { VALID_DATE_RANGES } from '../constants';

interface IUseViewOptionsProps {
  currentUserId: string | null;
  searchParams: URLSearchParams;
  onDefaultDateRangeApply: (dateRange: DateRangeType) => void;
}

export interface IUseViewOptionsResult {
  viewOptions: IRecruiterViewOptions;
  defaultDateRange: DateRangeType;
  handleViewOptionChange: (
    key: keyof IRecruiterViewOptions,
    checked: boolean
  ) => void;
  handleDefaultDateRangeChange: (value: DateRangeType) => void;
}

export function useViewOptions({
  currentUserId,
  searchParams,
  onDefaultDateRangeApply,
}: IUseViewOptionsProps): IUseViewOptionsResult {
  const [viewOptions, setViewOptions] = useState<IRecruiterViewOptions>(
    DEFAULT_RECRUITER_VIEW_OPTIONS
  );
  const [defaultDateRange, setDefaultDateRange] =
    useState<DateRangeType>('monthCal');

  const { data: settingsData } = useGetAppSettingsQuery({
    variables: { input: { settingsTab: RECRUITER_SETTINGS_TAB } },
  });
  const [saveSettings] = useSaveAppSettingsMutation({
    refetchQueries: [
      {
        query: GET_APP_SETTINGS_QUERY,
        variables: { input: { settingsTab: RECRUITER_SETTINGS_TAB } },
      },
    ],
  });

  useEffect(() => {
    if (settingsData?.getAppSettings?.settingsJson && currentUserId) {
      const json = settingsData.getAppSettings.settingsJson as Record<
        string,
        unknown
      >;
      const allUserOpts = json.userViewOptions as
        | Record<
            string,
            Partial<IRecruiterViewOptions> & { defaultDateRange?: string }
          >
        | undefined;
      const userOpts = allUserOpts?.[currentUserId];
      if (userOpts) {
        const { defaultDateRange: savedDR, ...visOpts } = userOpts;
        setViewOptions({ ...DEFAULT_RECRUITER_VIEW_OPTIONS, ...visOpts });
        if (savedDR && VALID_DATE_RANGES.has(savedDR)) {
          const dr = savedDR as DateRangeType;
          setDefaultDateRange(dr);
          if (!searchParams.get('dateRange')) {
            onDefaultDateRangeApply(dr);
          }
        }
      }
    }
  }, [settingsData, currentUserId]);

  const saveUserViewOptions = useCallback(
    (
      next: Partial<IRecruiterViewOptions> & {
        defaultDateRange?: DateRangeType;
      }
    ) => {
      if (!currentUserId) return;
      const existing =
        (settingsData?.getAppSettings?.settingsJson as Record<
          string,
          unknown
        >) || {};
      const prevUserOpts =
        ((existing.userViewOptions as Record<string, unknown>) || {})[
          currentUserId
        ] || {};
      saveSettings({
        variables: {
          input: {
            settingsTab: RECRUITER_SETTINGS_TAB,
            settingsJson: {
              ...existing,
              userViewOptions: {
                ...((existing.userViewOptions as Record<string, unknown>) ||
                  {}),
                [currentUserId]: { ...prevUserOpts, ...next },
              },
            },
          },
        },
      });
    },
    [currentUserId, settingsData, saveSettings]
  );

  const handleViewOptionChange = useCallback(
    (key: keyof IRecruiterViewOptions, checked: boolean) => {
      setViewOptions((prev) => {
        const next = { ...prev, [key]: checked };
        saveUserViewOptions(next);
        return next;
      });
    },
    [saveUserViewOptions]
  );

  const handleDefaultDateRangeChange = useCallback(
    (value: DateRangeType) => {
      setDefaultDateRange(value);
      onDefaultDateRangeApply(value);
      saveUserViewOptions({ defaultDateRange: value });
    },
    [onDefaultDateRangeApply, saveUserViewOptions]
  );

  return {
    viewOptions,
    defaultDateRange,
    handleViewOptionChange,
    handleDefaultDateRangeChange,
  };
}
